import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router";

import {
  Column,
  ErrorText,
  LoadingIndicator,
  TopbarContainer,
  TopbarTitle,
} from "@/components";
import { DemoConfigsForm } from "@/components/tools/DemoConfigsForm";

import { useSetTitle } from "@/hooks/useSetTitle";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS, MIXPANEL_SUBSCREENS } from "@/lib/mixpanel/types";
import { novaClient } from "@/lib/trpc";
import { CreateOrUpdateDemoConfigRequest, DemoConfig } from "@/types";

export const ToolsDemoConfigsEditPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  if (!id) {
    throw new Error("No ID found in URL for demo config edit page!");
  }

  const { data: demoConfig, status } =
    novaClient.internal.admin.demoConfigs.findOne.useQuery({
      id,
    });

  const { mutate: updateDemoConfig, isPending } =
    novaClient.internal.admin.demoConfigs.update.useMutation({
      onSuccess: () => {
        void navigate(`/tools/demo-configs/${id}`);
      },
    });

  useSetTitle({
    title: "Tools | Demo Configs | Edit",
  });

  useEffect(() => {
    mixpanelUtils.trackScreenView(MIXPANEL_SCREENS.DEMO_CONFIGS, {
      subScreenName: MIXPANEL_SUBSCREENS.EDIT,
    });
  }, []);

  const handleSubmit = (data: CreateOrUpdateDemoConfigRequest) => {
    updateDemoConfig({ id, ...data });
  };

  return (
    <Column className="h-svh">
      <TopbarContainer>
        <TopbarTitle className="!text-slate-500">
          <Link className="hover:underline" to="/tools/demo-configs">
            Demo Configs
          </Link>{" "}
          /{" "}
          <Link className="hover:underline" to={`/tools/demo-configs/${id}`}>
            {id}
          </Link>{" "}
          / <span className="text-slate-700">Edit</span>
        </TopbarTitle>
      </TopbarContainer>
      <div className="flex-1 overflow-y-auto p-6">
        {status === "pending" && <LoadingIndicator variant="medium" />}
        {status === "error" && (
          <ErrorText errorMessage="Error loading demo config." />
        )}
        {status === "success" && (
          <DemoConfigsForm
            demoConfig={demoConfig as DemoConfig}
            isSubmitting={isPending}
            onSubmit={handleSubmit}
            submitButtonText="Update"
          />
        )}
      </div>
    </Column>
  );
};
